import { useMemo, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { Box, Dialog, Typography, Button, Stack, DialogTitle, DialogContent, IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTranslation } from 'react-i18next';

import { InviteRequest } from 'types';
import { FormInput, ModalTitle, Spinner } from 'components';
import { withOutsideClickPreventer } from 'utils';
import { isHipaaLink } from 'config/productProfile';

import styles from './InvitePopup.module.scss';

type InvitePopupProps = {
  open: boolean;
  onInvite(model: InviteRequest): Promise<void>;
  onClose(): void;
};

export const InvitePopup = ({ open, onInvite, onClose }: InvitePopupProps) => {
  const { t } = useTranslation('settings');
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState<boolean>(false);
  const schema = useMemo(
    () =>
      Yup.object().shape({
        firstName: Yup.string().required(t('staffInvitePopup.firstNameRequired')),
        lastName: Yup.string().required(t('staffInvitePopup.lastNameRequired')),
        email: Yup.string().required(t('staffInvitePopup.emailRequired')).email(t('staffInvitePopup.emailInvalid')),
        via3UserType: Yup.mixed<'Staff' | 'Member'>()
          .oneOf(['Staff', 'Member'])
          .required(t('staffInvitePopup.typeRequired')),
        isLicenceActive: Yup.boolean(),
      }),
    [t],
  );

  const form = useForm<InviteRequest>({
    resolver: yupResolver(schema),
    defaultValues: {
      via3UserType: 'Staff',
    },
    mode: 'all',
  });

  const handleInvite = (): void => {
    setLoading(true);
    if (!form.formState.isValid) {
      return;
    }

    const formValues: InviteRequest = form.getValues();

    onInvite(formValues)
      .then(() => {
        form.reset({});
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || t('GENERIC_ERROR', { ns: 'errors' }), { variant: 'error' });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  useEffect(() => {
    if (isHipaaLink) {
      form.setValue('via3UserType', 'Staff', { shouldValidate: false });
    }
  }, [form]);

  return (
    <Box>
      <Dialog className={styles.dialog} open={open} onClose={withOutsideClickPreventer(onClose)}>
        {loading && <Spinner small />}
        <DialogTitle className={styles.dialogTitle}>
          <ModalTitle title={t('staffInvitePopup.inviteStaffUpper')} onClose={onClose} />
        </DialogTitle>

        <DialogContent className={styles.content}>
          <Stack className={styles.row}>
            <Typography variant='body2'>
              {t('staffInvitePopup.firstName')} <span className={styles.asterisk}>*</span>
            </Typography>
            <FormInput name='firstName' control={form.control} />
          </Stack>

          <Stack className={styles.row}>
            <Typography variant='body2'>
              {t('staffInvitePopup.lastName')} <span className={styles.asterisk}>*</span>
            </Typography>
            <FormInput name='lastName' control={form.control} />
          </Stack>

          <Stack className={styles.row}>
            <Typography variant='body2'>
              {t('staffInvitePopup.email')} <span className={styles.asterisk}>*</span>
            </Typography>
            <FormInput name='email' control={form.control} />
          </Stack>

          {!isHipaaLink && (
            <Stack className={styles.row}>
              <Typography variant='body2'>{t('staffInvitePopup.type')}</Typography>

              <Stack direction='row' gap='24px'>
                {/* STAFF */}
                <Stack
                  direction='row'
                  alignItems='center'
                  gap='8px'
                  onClick={() => form.setValue('via3UserType', 'Staff', { shouldValidate: true })}
                  className={styles.typeOption}
                >
                  <Box className={form.watch('via3UserType') === 'Staff' ? styles.bulletSelected : styles.bullet} />
                  <Typography variant='body2'>{t('staffInvitePopup.staff')}</Typography>

                  <IconButton
                    size='small'
                    onClick={(e) => e.stopPropagation()}
                    title={t('staffInvitePopup.fullFeatureAccess')}
                  >
                    <InfoOutlinedIcon fontSize='small' color='primary' />
                  </IconButton>
                </Stack>

                {/* MEMBER */}
                <Stack
                  direction='row'
                  alignItems='center'
                  gap='8px'
                  onClick={() => form.setValue('via3UserType', 'Member', { shouldValidate: true })}
                  className={styles.typeOption}
                >
                  <Box className={form.watch('via3UserType') === 'Member' ? styles.bulletSelected : styles.bullet} />
                  <Typography variant='body2'>{t('staffInvitePopup.member')}</Typography>

                  <IconButton
                    size='small'
                    onClick={(e) => e.stopPropagation()}
                    title={t('staffInvitePopup.limitedNoFaxAccess')}
                  >
                    <InfoOutlinedIcon fontSize='small' color='primary' />
                  </IconButton>
                </Stack>
              </Stack>
            </Stack>
          )}

          <Button variant='contained' disabled={!form.formState.isValid || loading} onClick={handleInvite}>
            {t('staffInvitePopup.send')}
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};
